/**
 * Notification Http Client
 * --------------------
 * @description Axios HTTP client configuration for notification API requests.
 *              Handles base URL, headers, authentication, and error interception.
 * @author Vikram Singh <vikram@maropost.com>
 * @created 2026-02
 */

import axios from 'axios'
import { AppConfig } from '@/lib/config/'
import keycloak from '../plugins/keycloak-config'
import { RequestInterceptors } from '../interceptors/request'

/**
 * Create a reusable Axios instance with common configuration.
 */
const $http = axios.create({
  baseURL: `${AppConfig.NOTIFICATION_BASE_URL}/api/v1`, 
  withCredentials: false,                             
  headers: {
    Accept: 'application/json'                        
  }
})

/**
 * Request Interceptors
 * - Attach account ID (via userStore).
 */
$http.interceptors.request.use(RequestInterceptors.AccountIdHandler)

/**
 * Request Interceptors
 * - Attach authentication token (via Keycloak).
 */
$http.interceptors.request.use(RequestInterceptors.TokenHandler)

/**
 * Response Interceptors
 * - Handle API responses and global error cases.
 */
$http.interceptors.response.use(
  /**
   * Success handler
   * @param {import('axios').AxiosResponse} response
   * @returns {import('axios').AxiosResponse}
   */
  (response) => response,

  /**
   * Error handler
   * @param {import('axios').AxiosError} error
   * @returns {Promise<never>}
   */
  (error) => {
    const data = error.response || error.request

    // ✅ Handle authentication issues
    switch (data?.status) {
      case 401: {
        if (!keycloak.authenticated) {
          keycloak.login() 
        }
        break
      }
    }

    return Promise.reject(error)
  }
)

export default $http
